/**
 * PageBreadcrumb — the horizontal navigation strip shown under DashboardHeader
 * on every new page. Matches the pattern used in Dashboard.js (line 282-303):
 *
 *   [ لوحة التحكم ]  <  [Section]  <  [ Current page (gold) ]
 *
 * Usage:
 *   <PageBreadcrumb items={[
 *     { label: 'AI Trading Hub', to: '/meta5-trading-hub' },
 *     { label: 'Manual Trading' },  // no `to` → current page (highlighted)
 *   ]} />
 */
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export default function PageBreadcrumb({ items = [] }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const homeLabel = isAr ? 'لوحة التحكم' : 'Dashboard';
  const sep = isAr ? '<' : '>';
  // Detect labels that start with "606" or contain latin — force LTR on them
  // so numbers stay on the LEFT regardless of page direction.
  const isLatin = (s) => /^[\s\d0-9]|^[A-Za-z\d]/.test(String(s || '').trim());

  return (
    <div className="border-b border-white/5" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <Link
            to="/dashboard"
            className="text-[#71717A] hover:text-[#d4af37] transition-colors"
            data-testid="breadcrumb-home"
          >
            {homeLabel}
          </Link>
          {items.map((item, idx) => {
            const isLast = idx === items.length - 1;
            const latin = isLatin(item.label);
            return (
              <span key={idx} className="flex items-center gap-2">
                <span className="text-[#71717A]">{sep}</span>
                {isLast || !item.to ? (
                  <span
                    className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5"
                    dir={latin ? 'ltr' : undefined}
                    data-testid={`breadcrumb-current`}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.to}
                    className="text-[#71717A] hover:text-[#d4af37] transition-colors"
                    dir={latin ? 'ltr' : undefined}
                    data-testid={`breadcrumb-${idx}`}
                  >
                    {item.label}
                  </Link>
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
